import React, { useState, useEffect } from 'react';
import { collection, onSnapshot, query, orderBy, limit } from 'firebase/firestore';
import { db } from '../lib/firebase';
import { Activity, User, FileText, Users, Settings, Eye, ArrowLeft } from 'lucide-react';

const ActivityLogs = ({ user, activeCompanyId, onBack }) => {
  const [logs, setLogs] = useState([]);
  const [filter, setFilter] = useState('all'); // all, document, user, settings
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user || !activeCompanyId) return;
    
    const logsQuery = query(
      collection(db, 'companies', activeCompanyId, 'activityLogs'),
      orderBy('createdAt', 'desc'),
      limit(100)
    );
    
    const unsubscribe = onSnapshot(logsQuery, (snapshot) => {
      const logsData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setLogs(logsData);
      setLoading(false);
    });
    
    return () => unsubscribe();
  }, [user, activeCompanyId]);

  const getActionIcon = (action) => {
    if (action.includes('document') || action.includes('quote') || action.includes('invoice')) {
      return <FileText size={16} className="text-blue-500" />;
    }
    if (action.includes('user') || action.includes('member')) {
      return <Users size={16} className="text-purple-500" />;
    }
    if (action.includes('settings')) {
      return <Settings size={16} className="text-orange-500" />;
    }
    return <Activity size={16} className="text-slate-500" />;
  };

  const getActionLabel = (action) => {
    const labels = {
      'create_quote': 'Devis créé',
      'create_invoice': 'Facture créée',
      'update_document': 'Document modifié',
      'send_email': 'Email envoyé',
      'create_client': 'Client créé',
      'update_client': 'Client modifié',
      'delete_client': 'Client supprimé',
      'toggle_dark_mode': 'Mode sombre',
      'export_csv': 'Export CSV',
      'login': 'Connexion',
      'logout': 'Déconnexion'
    };
    return labels[action] || action;
  };

  const filteredLogs = logs.filter(log => {
    if (filter === 'all') return true;
    if (filter === 'document') return log.action.includes('quote') || log.action.includes('invoice') || log.action.includes('document');
    if (filter === 'user') return log.action.includes('user') || log.action.includes('member') || log.action.includes('client');
    if (filter === 'settings') return log.action.includes('settings') || log.action.includes('dark_mode');
    return true;
  });

  return (
    <div className="max-w-6xl mx-auto p-4 md:p-6 animate-page pb-24">
      <div className="flex items-center gap-4 mb-8">
        <button 
          onClick={onBack}
          className="p-2 hover:bg-slate-100 rounded-lg transition"
        >
          <ArrowLeft size={20} className="text-slate-600" />
        </button>
        <div>
          <h2 className="text-2xl font-bold text-slate-800 flex gap-3 items-center mb-1">
            <div className="bg-indigo-100 p-2 rounded-xl">
              <Activity className="text-indigo-600" size={24}/>
            </div>
            Journal d'Activité
          </h2>
          <p className="text-slate-500 text-sm">Historique des 100 dernières actions sur votre compte.</p>
        </div>
      </div>

      {/* Filtres */}
      <div className="flex gap-2 mb-6 bg-white p-1 rounded-xl border border-slate-200 w-fit">
        {[
          { id: 'all', label: 'Tout' },
          { id: 'document', label: 'Documents' },
          { id: 'user', label: 'Utilisateurs' },
          { id: 'settings', label: 'Paramètres' }
        ].map(f => (
          <button
            key={f.id}
            onClick={() => setFilter(f.id)}
            className={`px-4 py-2 rounded-lg text-sm font-bold transition ${
              filter === f.id
                ? 'bg-indigo-500 text-white'
                : 'text-slate-600 hover:bg-slate-50'
            }`}
          >
            {f.label}
          </button>
        ))}
      </div>

      {/* Liste des logs */}
      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
        {loading ? (
          <div className="p-12 text-center text-slate-400">
            Chargement...
          </div>
        ) : filteredLogs.length === 0 ? (
          <div className="p-12 text-center text-slate-400">
            <Eye size={48} className="mx-auto mb-4 opacity-20" />
            <p>Aucune activité enregistrée</p>
          </div>
        ) : (
          <div className="divide-y divide-slate-100">
            {filteredLogs.map((log) => (
              <div key={log.id} className="p-4 hover:bg-slate-50 transition flex items-start gap-4">
                <div className="w-10 h-10 bg-slate-100 rounded-full flex items-center justify-center flex-shrink-0">
                  {getActionIcon(log.action)}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-4 mb-1">
                    <h3 className="font-bold text-slate-800 text-sm">
                      {getActionLabel(log.action)}
                    </h3>
                    <span className="text-xs text-slate-400 flex-shrink-0">
                      {log.timestamp ? new Date(log.timestamp).toLocaleString('fr-FR', {
                        day: '2-digit',
                        month: '2-digit',
                        year: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit'
                      }) : 'Date inconnue'}
                    </span>
                  </div>
                  <div className="flex items-center gap-2 text-xs text-slate-500 mb-2">
                    <User size={12} />
                    <span>{log.userEmail || 'Utilisateur inconnu'}</span>
                  </div>
                  {log.details && Object.keys(log.details).length > 0 && (
                    <div className="text-xs text-slate-600 bg-slate-50 p-2 rounded-lg mt-2">
                      {Object.entries(log.details).map(([key, value]) => (
                        <div key={key} className="flex gap-2">
                          <span className="font-medium">{key}:</span>
                          <span>{typeof value === 'boolean' ? (value ? 'Oui' : 'Non') : String(value)}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default ActivityLogs;
